import { TDatabaseId } from '@infotech-soe/metaschema';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { WorkTableData } from '../../models/work-table/WorkTableData';
import { WorkTableType } from '../../services/commands/UserInteractor';
import { ClientContext } from '../../services/context/IContextService';
import UniqueId from '../../services/uid/UniqueId';

import { failureAction, pendingAction, successAction } from './actionHandler';

export const enum OpenWorkTablePostEffectType {
  NOTHING = 'nothing',
  REPLACE = 'replace',
  CHANGE_DATE = 'changeDate',
  FORMAT = 'format',
  UPDATE = 'update',
}

interface OpenWorkTableNothingPostEffect {
  type: OpenWorkTablePostEffectType.NOTHING;
}

interface OpenWorkTableReplacePostEffect {
  type: OpenWorkTablePostEffectType.REPLACE;
}

interface OpenWorkTableChangeDatePostEffect {
  type: OpenWorkTablePostEffectType.CHANGE_DATE;
  workerId: TDatabaseId;
  newDate: { month: number; year: number };
}

interface OpenWorkTableFormatPostEffect {
  type: OpenWorkTablePostEffectType.FORMAT;
  workerId: TDatabaseId;
}

interface OpenWorkTableUpdatePostEffect {
  type: OpenWorkTablePostEffectType.UPDATE;
  workerId: TDatabaseId;
  data: WorkTableData;
}

export type OpenWorkTablePostEffect =
  | OpenWorkTableNothingPostEffect
  | OpenWorkTableReplacePostEffect
  | OpenWorkTableChangeDatePostEffect
  | OpenWorkTableFormatPostEffect
  | OpenWorkTableUpdatePostEffect;

export interface WorkTableState {
  readonly workerId: TDatabaseId;
  readonly context: ClientContext;
  readonly tableType: WorkTableType;
  readonly datasetId: UniqueId;
  readonly data: WorkTableData;
  readonly changed: boolean;
  readonly show: boolean;
}

const initialState: WorkTableState = {
  workerId: 1,
  context: new ClientContext(),
  tableType: WorkTableType.PLAN,
  datasetId: '',
  data: {
    startDate: new Date(),
    endDate: new Date(),
    workDays: [],
    workHours: {},
    postPerform: { actFrom: null, actTill: null, scheduleCode: null, id: 1 },
    norm: { hours: 1, days: 1 },
  },
  changed: false,
  show: false,
};

const colorBGRToHex = (color: number): string =>
  '#' +
  // eslint-disable-next-line no-bitwise
  (((color & 0xff0000) >> 16) | (color & 0x00ff00) | ((color & 0x0000ff) << 16))
    .toString(16)
    .padStart(6, '0');

const workTableSlice = createSlice({
  name: 'workTable',
  initialState,
  reducers: {
    open: pendingAction<
      WorkTableState,
      {
        datasetId: UniqueId;
        tableType: WorkTableType;
        effect: OpenWorkTablePostEffect;
      }
    >(),
    openSuccess: successAction<
      WorkTableState,
      {
        context: ClientContext;
        tableType: WorkTableType;
        data: WorkTableData;
        datasetId: UniqueId;
        effect: OpenWorkTablePostEffect;
      }
    >((state, { payload }) => {
      const { effect, datasetId, context } = payload;

      if (effect.type === OpenWorkTablePostEffectType.UPDATE) {
        state.data = effect.data;

        return;
      }

      return {
        ...payload,
        workerId: context.get('Id'),
        show: true,
        datasetId,
        changed: false,
      };
    }),
    openFailure: failureAction(),
    close() {
      return initialState;
    },
    update: pendingAction(),
    updateSuccess: successAction(),
    updateFailure: failureAction(),
    changed(state) {
      state.changed = true;
    },
    updateWorkDays(
      state,
      {
        payload: { selectedDays, outParams },
      }: PayloadAction<{
        selectedDays: number[];
        tableId: TDatabaseId;
        outParams: {
          code: string;
          absenceSign: number;
          absenceSignPlan: number;
          color: number;
        };
      }>
    ) {
      state.data.workDays = state.data.workDays.map(day =>
        selectedDays.includes(day.day)
          ? {
              isAbsence:
                state.tableType === WorkTableType.FACT
                  ? Boolean(outParams.absenceSign)
                  : Boolean(outParams.absenceSignPlan),
              day: day.day,
              dayTypeCode: outParams.code,
              dayTypeName: day.dayTypeName,
              color: colorBGRToHex(outParams.color),
              isRestricted: day.isRestricted,
            }
          : day
      );
    },
    deleteWorkDays(state, { payload }: PayloadAction<number[]>) {
      state.data.workDays = state.data.workDays.map(day =>
        payload.includes(day.day)
          ? {
              isAbsence: false,
              day: day.day,
              dayTypeCode: null,
              color: '',
              isRestricted: day.isRestricted,
            }
          : day
      );
    },
    updateWorkHours(
      state,
      {
        payload: { hourTypeId, day, hours },
      }: PayloadAction<{
        workerId: TDatabaseId;
        hourTypeId: number;
        day: number;
        hours: number;
      }>
    ) {
      const validHours = Number.isNaN(hours) ? 0 : hours;
      const workHours = state.data.workHours;
      state.changed = true;
      for (const [name, hour] of Object.entries(workHours)) {
        if (hour.hourTypeId !== hourTypeId) {
          continue;
        }

        const worksByDay = workHours[name].worksByDay;
        const existingDay = worksByDay.find(d => d.day === day);

        if (existingDay) {
          existingDay.hours = validHours;
        } else {
          worksByDay.push({ day, hours: validHours });
        }

        break;
      }
    },
    createWorkHours(
      state,
      {
        payload: { outParams },
      }: PayloadAction<{
        workerId: TDatabaseId;
        outParams: { regno: number; numb: number; code: string };
      }>
    ) {
      state.changed = true;
      state.data.workHours[outParams.code] = {
        hourTypeId: outParams.regno,
        hourTypeNo: outParams.numb,
        hourTypeCode: outParams.code,
        isBase: false,
        worksByDay: [],
      };
    },
  },
});

export const workTableReducer = workTableSlice.reducer;

export default workTableSlice.actions;
